/**
 * Capa de datos de ubicaciones dentro de las zonas (racks, niveles, posiciones).
 * Tablas y RPC: supabase/migrations/20261013000000_ubicaciones_racks_schema.sql
 * y 20261013010000_create_rack_with_grid.sql. La empresa la fija un trigger
 * (zona → bodega → empresa): nunca se envía company_id desde aquí.
 *
 * No se usan embebidos de PostgREST entre racks, niveles y posiciones:
 * product_locations crea una segunda relación entre esas tablas y la API
 * podría rechazar el embebido por ambiguo. Se consulta plano y se arma aquí.
 */
import { supabase } from '../supabaseClient';
import { codigoCompleto } from './ubicaciones-codigos';

export interface RackPosition { id: string; position_number: number; code: string }
export interface RackLevel { id: string; level_number: number; code: string; positions: RackPosition[] }
export interface Rack { id: string; zone_id: string; code: string; low_levels: number; notes: string | null; levels: RackLevel[] }

export interface ProductLocation {
  id: string;
  product_id: string;
  zone_id: string;
  rack_id: string;
  level_id: string | null;
  position_id: string | null;
}

interface RackRow { id: string; zone_id: string; code: string; low_levels: number; notes: string | null }
interface LevelRow { id: string; rack_id: string; level_number: number; code: string }
interface PositionRow { id: string; level_id: string; position_number: number; code: string }

const PAGINA = 1000;

/** Trae todas las filas de una consulta en páginas de 1 000 (máximo de la API). */
async function todasLasFilas<T>(consulta: (desde: number, hasta: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>): Promise<T[]> {
  const filas: T[] = [];
  for (let desde = 0; ; desde += PAGINA) {
    const { data, error } = await consulta(desde, desde + PAGINA - 1);
    if (error) throw new Error(error.message);
    filas.push(...(data ?? []));
    if (!data || data.length < PAGINA) return filas;
  }
}

// ── Funciones puras (probadas en ubicaciones.test.ts) ─────────────────────────

/** Arma racks → niveles (N1 primero) → posiciones (P01 primero), ordenados. */
export function armarEstructura(racks: RackRow[], niveles: LevelRow[], posiciones: PositionRow[]): Rack[] {
  const posPorNivel = new Map<string, RackPosition[]>();
  for (const p of posiciones) {
    const lista = posPorNivel.get(p.level_id) ?? [];
    lista.push({ id: p.id, position_number: p.position_number, code: p.code });
    posPorNivel.set(p.level_id, lista);
  }
  const nivPorRack = new Map<string, RackLevel[]>();
  for (const l of niveles) {
    const lista = nivPorRack.get(l.rack_id) ?? [];
    lista.push({
      id: l.id, level_number: l.level_number, code: l.code,
      positions: (posPorNivel.get(l.id) ?? []).sort((a, b) => a.position_number - b.position_number),
    });
    nivPorRack.set(l.rack_id, lista);
  }
  return racks
    .map(r => ({ ...r, levels: (nivPorRack.get(r.id) ?? []).sort((a, b) => a.level_number - b.level_number) }))
    .sort((a, b) => a.code.localeCompare(b.code, 'es', { numeric: true, sensitivity: 'base' }));
}

/** Productos distintos asignados a un rack (en cualquier nivel o posición). */
export function productosDelRack(ubicaciones: ProductLocation[], rackId: string): string[] {
  return Array.from(new Set(ubicaciones.filter(u => u.rack_id === rackId).map(u => u.product_id)));
}

export interface ZonaInfo { id: string; name: string; code: string | null; warehouse_id: string | null; product_ids: string[] | null }
export interface UbicacionDescrita {
  /** Texto para el chip: "Bodega › Zona A › Z1-R03-N2-P05" o "Bodega › Zona B (sin rack)". */
  etiqueta: string;
  zonaId: string;
  /** Código completo (null si el producto está en la zona sin rack). */
  codigo: string | null;
}

/**
 * Ubicaciones de un producto: una por cada asignación a rack/nivel/posición y,
 * en las zonas donde está sin ninguna asignación, "(sin rack)".
 */
export function describirUbicaciones(args: {
  productId: string;
  zonas: ZonaInfo[];
  ubicaciones: ProductLocation[];
  bodegas: Map<string, string>;
  racks: Map<string, string>;
  niveles: Map<string, string>;
  posiciones: Map<string, string>;
}): UbicacionDescrita[] {
  const { productId, zonas, ubicaciones, bodegas, racks, niveles, posiciones } = args;
  const propias = ubicaciones.filter(u => u.product_id === productId);
  const resultado: UbicacionDescrita[] = [];
  const zonasOrdenadas = [...zonas]
    .filter(z => (z.product_ids ?? []).includes(productId) || propias.some(u => u.zone_id === z.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  for (const z of zonasOrdenadas) {
    const prefijo = [z.warehouse_id ? bodegas.get(z.warehouse_id) : null, z.name].filter(Boolean).join(' › ');
    const enZona = propias.filter(u => u.zone_id === z.id);
    if (enZona.length === 0) {
      resultado.push({ etiqueta: `${prefijo} (sin rack)`, zonaId: z.id, codigo: null });
      continue;
    }
    const codigos = enZona.map(u => codigoCompleto({
      zona: z.code,
      rack: racks.get(u.rack_id) ?? '?',
      nivel: u.level_id ? niveles.get(u.level_id) ?? '?' : null,
      posicion: u.position_id ? posiciones.get(u.position_id) ?? '?' : null,
    })).sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
    for (const codigo of codigos) resultado.push({ etiqueta: `${prefijo} › ${codigo}`, zonaId: z.id, codigo });
  }
  return resultado;
}

// ── Lectura ───────────────────────────────────────────────────────────────────

/** Racks de una zona con sus niveles y posiciones. */
export async function fetchEstructuraZona(zoneId: string): Promise<Rack[]> {
  const { data: racks, error } = await supabase
    .from('warehouse_racks').select('id, zone_id, code, low_levels, notes').eq('zone_id', zoneId);
  if (error) throw new Error(error.message);
  const porRack = await Promise.all((racks ?? []).map(async (r: RackRow) => {
    const { data: niveles, error: e1 } = await supabase
      .from('warehouse_rack_levels').select('id, rack_id, level_number, code').eq('rack_id', r.id);
    if (e1) throw new Error(e1.message);
    const ids = (niveles ?? []).map((l: LevelRow) => l.id);
    const posiciones = ids.length === 0 ? [] : await todasLasFilas<PositionRow>((desde, hasta) =>
      supabase.from('warehouse_rack_positions').select('id, level_id, position_number, code')
        .in('level_id', ids).order('id').range(desde, hasta));
    return { niveles: (niveles ?? []) as LevelRow[], posiciones };
  }));
  return armarEstructura(
    (racks ?? []) as RackRow[],
    porRack.flatMap(x => x.niveles),
    porRack.flatMap(x => x.posiciones),
  );
}

export async function fetchUbicacionesZona(zoneId: string): Promise<ProductLocation[]> {
  return todasLasFilas<ProductLocation>((desde, hasta) =>
    supabase.from('product_locations').select('id, product_id, zone_id, rack_id, level_id, position_id')
      .eq('zone_id', zoneId).order('id').range(desde, hasta));
}

/** Ubicaciones descritas de una lista de productos (p. ej. la página de Productos). */
export async function fetchUbicacionesDeProductos(productIds: string[]): Promise<Map<string, UbicacionDescrita[]>> {
  const resultado = new Map<string, UbicacionDescrita[]>();
  if (productIds.length === 0) return resultado;

  const [{ data: zonas, error: e1 }, ubicaciones] = await Promise.all([
    supabase.from('warehouse_zones').select('id, name, code, warehouse_id, product_ids').overlaps('product_ids', productIds),
    todasLasFilas<ProductLocation>((desde, hasta) =>
      supabase.from('product_locations').select('id, product_id, zone_id, rack_id, level_id, position_id')
        .in('product_id', productIds).order('id').range(desde, hasta)),
  ]);
  if (e1) throw new Error(e1.message);

  const zonasLista = (zonas ?? []) as ZonaInfo[];
  const faltantes = Array.from(new Set(ubicaciones.map(u => u.zone_id))).filter(id => !zonasLista.some(z => z.id === id));
  if (faltantes.length) {
    const { data, error } = await supabase.from('warehouse_zones').select('id, name, code, warehouse_id, product_ids').in('id', faltantes);
    if (error) throw new Error(error.message);
    zonasLista.push(...((data ?? []) as ZonaInfo[]));
  }

  const idsDe = (k: 'rack_id' | 'level_id' | 'position_id') => Array.from(new Set(ubicaciones.map(u => u[k]).filter((x): x is string => !!x)));
  const bodegaIds = Array.from(new Set(zonasLista.map(z => z.warehouse_id).filter((x): x is string => !!x)));
  const vacio = Promise.resolve({ data: [] as { id: string; code?: string; name?: string }[], error: null });
  const [rRacks, rNiveles, rPos, rBodegas] = await Promise.all([
    idsDe('rack_id').length ? supabase.from('warehouse_racks').select('id, code').in('id', idsDe('rack_id')) : vacio,
    idsDe('level_id').length ? supabase.from('warehouse_rack_levels').select('id, code').in('id', idsDe('level_id')) : vacio,
    idsDe('position_id').length ? supabase.from('warehouse_rack_positions').select('id, code').in('id', idsDe('position_id')) : vacio,
    bodegaIds.length ? supabase.from('warehouses').select('id, name').in('id', bodegaIds) : vacio,
  ]);
  for (const r of [rRacks, rNiveles, rPos, rBodegas]) if (r.error) throw new Error(r.error.message);
  const mapa = (rows: { id: string; code?: string; name?: string }[] | null, campo: 'code' | 'name') =>
    new Map((rows ?? []).map(x => [x.id, String(x[campo] ?? '')]));

  const racks = mapa(rRacks.data, 'code'), niveles = mapa(rNiveles.data, 'code');
  const posiciones = mapa(rPos.data, 'code'), bodegas = mapa(rBodegas.data, 'name');
  for (const productId of productIds) {
    resultado.set(productId, describirUbicaciones({ productId, zonas: zonasLista, ubicaciones, bodegas, racks, niveles, posiciones }));
  }
  return resultado;
}

// ── Escritura (RLS: crear/editar admin y manager; borrar solo admin) ──────────

export async function crearRack(zoneId: string, codigo: string, niveles: number, posiciones: number, nivelesBajos: number): Promise<string> {
  const { data, error } = await supabase.rpc('create_rack_with_grid', {
    p_zone_id: zoneId, p_code: codigo.trim(), p_levels: niveles, p_positions: posiciones, p_low_levels: nivelesBajos,
  });
  if (error) throw new Error(mensajeAmigable(error.message));
  return data as string;
}

export async function actualizarRack(rackId: string, cambios: { code?: string; low_levels?: number }): Promise<void> {
  const { error } = await supabase.from('warehouse_racks').update(cambios).eq('id', rackId);
  if (error) throw new Error(mensajeAmigable(error.message));
}

export async function actualizarCodigoNivel(levelId: string, code: string): Promise<void> {
  const { error } = await supabase.from('warehouse_rack_levels').update({ code: code.trim() }).eq('id', levelId);
  if (error) throw new Error(mensajeAmigable(error.message));
}

export async function actualizarCodigoPosicion(positionId: string, code: string): Promise<void> {
  const { error } = await supabase.from('warehouse_rack_positions').update({ code: code.trim() }).eq('id', positionId);
  if (error) throw new Error(mensajeAmigable(error.message));
}

export async function borrarRack(rackId: string): Promise<void> {
  const { error } = await supabase.from('warehouse_racks').delete().eq('id', rackId);
  if (error) throw new Error(mensajeAmigable(error.message));
}

export async function asignarProductos(
  destino: { zoneId: string; rackId: string; levelId: string | null; positionId: string | null },
  productIds: string[],
): Promise<void> {
  if (productIds.length === 0) return;
  const filas = productIds.map(product_id => ({
    product_id, zone_id: destino.zoneId, rack_id: destino.rackId, level_id: destino.levelId, position_id: destino.positionId,
  }));
  const { error } = await supabase.from('product_locations').insert(filas);
  if (error) throw new Error(mensajeAmigable(error.message));
}

export async function quitarUbicacion(locationId: string): Promise<void> {
  const { error } = await supabase.from('product_locations').delete().eq('id', locationId);
  if (error) throw new Error(mensajeAmigable(error.message));
}

/** Traduce los errores esperables de la base a mensajes para el usuario. */
export function mensajeAmigable(msg: string): string {
  if (/warehouse_racks_zone_code_uniq/.test(msg)) return 'Ya existe un rack con ese código en esta zona.';
  if (/warehouse_rack_levels_code_uniq/.test(msg)) return 'Ya existe un nivel con ese código en este rack.';
  if (/warehouse_rack_positions_code_uniq/.test(msg)) return 'Ya existe una posición con ese código en este nivel.';
  if (/warehouse_zones_wh_code_uniq/.test(msg)) return 'Ya existe una zona con ese código en esta bodega.';
  if (/product_locations_product_id_rack_id_level_id_position_id_key/.test(msg)) return 'El producto ya está en esa ubicación.';
  if (/row-level security|permission denied/i.test(msg)) return 'Tu rol no tiene permiso para esta acción.';
  return msg;
}
